import * as THREE from 'three';
import { OrbitControls, Environment } from '@react-three/drei';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Suspense, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useStore } from './store';
import XButton from './components/XButton';
import DexscreenerButton from './components/DexscreenerButton';
import LookbookButton from './components/LBButton';
import SubmitFitPicButton from './components/SubmitFitPicButton';
import LookbookModal from './components/LookbookModal';
import solanaLogo from "./img/solanaLogo.svg"

import HomepageLogo from './components/HomepageLogo';
// import DayScheduleButton from './components/DaySchedule';
import AudioControls from './components/AudioControls';  
import './App.css';

const IDLE_TIMER = 3000;

function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const isMobileDevice = isMobile();

const LoadingBar = ({ progress }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      <div className="w-64 h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-white transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="mt-4 text-white/50 text-sm font-medium">
        {Math.round(progress)}%
      </div>
    </div>
  );
};

const checkAssetExists = (url) => {
  return fetch(url, { method: 'HEAD' })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return true;
    })
    .catch(error => {
      console.warn(`Asset check failed for ${url}:`, error);
      return false;
    });
};

const AssetPreloader = ({ onLoadComplete, children }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const assets = [
      '/environment/studio.hdr',  // Updated to use local HDR file
      '/bgm/bgm.mp3'
    ];

    let loadedCount = 0;

    const loadAssets = async () => {
      try {
        const loadPromises = assets.map((url) => {
          return checkAssetExists(url).then(() => {
            loadedCount++;
            setLoadingProgress((loadedCount / assets.length) * 100);
          });
        });

        await Promise.all(loadPromises);
        setIsLoading(false);
        if (onLoadComplete) onLoadComplete();
      } catch (error) {
        console.error('Error preloading assets:', error);
        setTimeout(() => {
          setIsLoading(false);
          if (onLoadComplete) onLoadComplete();
        }, 2000);
      }
    };

    loadAssets();
  }, [onLoadComplete]);

  return (
    <>
      {isLoading && <LoadingBar progress={loadingProgress} />}
      {children}
    </>
  );
};

function MobileEnvironment() {
  const { scene, gl } = useThree();
  
  useEffect(() => {
    // Using the RGBELoader to load HDR for mobile as well
    const loadHDR = async () => {
      const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js');
      
      new RGBELoader()
        .setPath('/environment/')
        .load('studio.hdr', (texture) => {
          texture.mapping = THREE.EquirectangularReflectionMapping;
          
          scene.background = texture;
          scene.environment = texture;
          
          gl.physicallyCorrectLights = true;
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1;
        }, 
        undefined, 
        (error) => {
          console.error('Error loading HDR:', error);
        });
    };
    
    loadHDR();
    
    return () => {
      if (scene.background) {
        scene.background.dispose();
        scene.background = null;
      }
      if (scene.environment) {
        scene.environment.dispose();
        scene.environment = null;
      }
    };
  }, [scene, gl]);

  return null;
}

function EnvironmentLoader() {
  if (isMobileDevice) {
    return (
      <>
        <MobileEnvironment />
        <MobileLighting />
      </>
    );
  }

  // Using local HDR file
  return (
    <Environment 
      files="/environment/studio.hdr"
      background
      blur={0.1}
      intensity={0.85}
    />
  );
}

function MobileLighting() {
  if (!isMobileDevice) return null;
  
  return (
    <>
      <ambientLight intensity={1.0} />
      <pointLight position={[10, 10, 10]} intensity={3.0} color="#FFFFFF" />
      <pointLight position={[-10, 5, -10]} intensity={1.5} color="#9370DB" />
      <pointLight position={[0, -10, 0]} intensity={1.0} color="#4B0082" />
    </>
  );
}

function useOrientation() {
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isPortrait;
}

const mobileSettings = {
  cameraZ: 12,
  fov: 60,
  scale: 0.75
};

const desktopSettings = {
  cameraZ: 8,
  fov: 50,
  scale: 1
};

function CameraControls({ mobileSettings, desktopSettings }) {
  const { camera } = useThree();
  const controls = useRef();
  const isAnimating = useStore((state) => state.isAnimating);
  const isModelAnimationComplete = useStore((state) => state.isModelAnimationComplete);
  const setModelAnimationComplete = useStore((state) => state.setModelAnimationComplete);
  
  // Animation states
  const [isRotating, setIsRotating] = useState(false);
  const [isOrbiting, setIsOrbiting] = useState(false);
  const [idleTimerActive, setIdleTimerActive] = useState(false);
  
  // Refs for animation control
  const startAngle = useRef(0);
  const elapsedTime = useRef(0);
  const orbitAngle = useRef(0);
  const lastInteractionTime = useRef(Date.now());
  const hasAnimated = useRef(false);
  const isPortrait = useOrientation();
  const lastCameraPosition = useRef(new THREE.Vector3());
  
  const baseRadius = isPortrait ? mobileSettings.cameraZ : desktopSettings.cameraZ;
  
  const springVelocity = useRef(0);
  const springTarget = useRef(0);
  const springCurrent = useRef(0);

  const easeInOutCubic = (x) => {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  };

  const easeOutBack = (x) => {
    const c1 = 1.20158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
  };

  const spring = useMemo(() => ({
    tension: 0.15,
    friction: 0.9,
    velocity: springVelocity,
    target: springTarget,
    current: springCurrent
  }), []); 


  // Start the initial animation when isAnimating becomes true
  useEffect(() => {
    if (isAnimating && !isRotating && !hasAnimated.current) {
      hasAnimated.current = true; 
      setTimeout(() => {
        setIsRotating(true);
        startAngle.current = Math.atan2(camera.position.z, camera.position.x);
        elapsedTime.current = 0;
        spring.current.current = startAngle.current;
        spring.target.current = startAngle.current + Math.PI * 2;
      }, 2000);
    }
  }, [isAnimating, camera, isRotating, spring]);


  // Function to reset the idle timer - only called when camera actually moves
  const resetIdleTimer = useCallback(() => {
    lastInteractionTime.current = Date.now();
    
    // If we're currently orbiting, stop it and enable controls
    if (isOrbiting) {
      setIsOrbiting(false);
      if (controls.current) {
        controls.current.enabled = true;
      }
    }
    
    // Store the new camera position for change detection
    lastCameraPosition.current.copy(camera.position);
  }, [isOrbiting, camera]);


  // Start tracking idle time after initial animation completes
  useEffect(() => {
    if (isModelAnimationComplete && !idleTimerActive) {
      setIdleTimerActive(true);
      lastInteractionTime.current = Date.now(); // Initialize the timer
      lastCameraPosition.current.copy(camera.position); // Store initial position
    }
  }, [isModelAnimationComplete, idleTimerActive, camera]);


  // Attach onChange handler to OrbitControls
  useEffect(() => {
    if (controls.current && idleTimerActive) {
      // Store the original onChange handler if it exists
      const originalOnChange = controls.current.onChange;
      // Create a reference to controls.current for cleanup function
      const currentControls = controls.current;
      
      // Create our custom onChange handler
      currentControls.onChange = () => {
        // Check if camera position has actually changed
        if (!camera.position.equals(lastCameraPosition.current)) {
          resetIdleTimer();
        }
        
        // Call original onChange if it exists
        if (originalOnChange) originalOnChange();
      };
      
      return () => {
        // Use the captured reference in cleanup
        if (currentControls) {
          currentControls.onChange = originalOnChange;
        }
      };
    }
  }, [controls, idleTimerActive, resetIdleTimer, camera]);


  useFrame((state, delta) => {
    // Initial rotation animation
    if (isRotating) {
      elapsedTime.current += delta;
      const duration = 5;
      const progress = Math.min(elapsedTime.current / duration, 1);

      if (progress < 1) {
        const easedProgress = progress < 0.8 
          ? easeInOutCubic(progress / 0.8) 
          : easeOutBack((progress - 0.8) / 0.2);
          
        const angle = startAngle.current + (Math.PI * 2 * easedProgress);
        const heightOffset = Math.sin(progress * Math.PI) * 0.2;
        const radius = baseRadius + Math.sin(progress * Math.PI * 2) * 0.2;
        
        camera.position.x = radius * Math.cos(angle);
        camera.position.z = radius * Math.sin(angle);
        camera.position.y = heightOffset;
        
        const lookAtScale = Math.max(0, 1 - (progress * 2 - 1));
        const lookAtX = Math.sin(angle * 0.05) * 0.1 * lookAtScale;
        const lookAtZ = Math.cos(angle * 0.05) * 0.1 * lookAtScale;
        camera.lookAt(lookAtX, heightOffset * 0.3 * lookAtScale, lookAtZ);
        
        if (controls.current) {
          controls.current.enabled = false;
        }

        if (progress > 0.9) {
          const endEase = easeInOutCubic((progress - 0.9) / 0.1);
          camera.position.y *= (1 - endEase * 0.5);
        }
      } else {
        setIsRotating(false);
        if (controls.current) {
          controls.current.enabled = true;
        }
        setModelAnimationComplete();
        // Store initial camera position after animation completes
        lastCameraPosition.current.copy(camera.position);
      }
    }
    
    // Check for idle time to potentially start orbiting
    if (idleTimerActive && !isRotating && !isOrbiting) {
      const currentTime = Date.now();
      const timeSinceLastInteraction = currentTime - lastInteractionTime.current;
      
      // Start orbiting after 3 seconds of camera inactivity
      if (timeSinceLastInteraction >= IDLE_TIMER) {
        // Calculate the current camera angle to start orbiting from current position
        orbitAngle.current = Math.atan2(camera.position.z, camera.position.x);
        setIsOrbiting(true);
        
        // Disable controls during orbiting
        if (controls.current) {
          controls.current.enabled = false;
        }
      }
    }
    
    // Continuous orbital rotation animation (during idle periods)
    if (isOrbiting && !isRotating) {
      // Calculate angle for a complete 360° rotation in 8 seconds
      const orbitalSpeed = (Math.PI * 2) / 8; // 2π radians / 8 seconds
      
      // Update the orbit angle - continue from the current angle
      orbitAngle.current += delta * orbitalSpeed;
      
      // Get current radius (distance from center)
      const currentRadius = Math.sqrt(
        camera.position.x * camera.position.x + 
        camera.position.z * camera.position.z
      );
      
      // Apply smooth continuous orbit while maintaining current height and distance
      camera.position.x = currentRadius * Math.cos(orbitAngle.current);
      camera.position.z = currentRadius * Math.sin(orbitAngle.current);
      
      // Always look at the center
      camera.lookAt(0, 0, 0);
    }
  });


  // Monitor camera movement directly through wheel and pointer events
  useEffect(() => {
    if (!idleTimerActive) return;
    
    // These specific events can affect camera position
    const handleCameraMovement = (e) => {
      // Only for specific events that are used by OrbitControls to move the camera
      if (
        e.type === 'wheel' || 
        (e.type.includes('mouse') && e.buttons > 0) || 
        e.type.includes('touch')
      ) {
        // Check in the next frame if camera position actually changed
        requestAnimationFrame(() => {
          if (!camera.position.equals(lastCameraPosition.current)) {
            resetIdleTimer();
          }
        });
      }
    };


    // Camera-specific movement events
    const cameraEvents = ['wheel', 'mousedown', 'mousemove', 'mouseup', 'touchstart', 'touchmove', 'touchend'];
    
    cameraEvents.forEach(event => {
      window.addEventListener(event, handleCameraMovement, { passive: true });
    });
    
    return () => {
      cameraEvents.forEach(event => {
        window.removeEventListener(event, handleCameraMovement);
      });
    };
  }, [idleTimerActive, resetIdleTimer, camera]);


  return (
    <OrbitControls
      ref={controls}
      enableZoom={true}
      enablePan={false}
      rotateSpeed={0.5}
      minDistance={isPortrait ? mobileSettings.cameraZ - 4 : desktopSettings.cameraZ - 3}
      maxDistance={isPortrait ? mobileSettings.cameraZ + 4 : desktopSettings.cameraZ + 7}
      dampingFactor={0.05}
      enableDamping={true}
    />
  );
}


function EnvironmentRotation() {
  const { scene } = useThree();
  const [isAnimating] = useState(true);
  
  useEffect(() => {
    const envNode = scene.children.find(child => child.name === 'Environment');
    if (!envNode) return;

    const rotationSpeed = 0.001;
    let animationId;

    const animate = () => {
      if (isAnimating && envNode) {
        envNode.rotation.y += rotationSpeed;
      }
      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [scene, isAnimating]);

  return null;
}


function LoadingOverlay() {
  const isAnimating = useStore((state) => state.isAnimating);
  
  return (
    <div 
      className={`fixed inset-0 bg-black z-40 transition-opacity duration-1000 ${
        isAnimating ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    />
  );
}



const EnterButton = ({ isVisible }) => {
  const startAnimation = useStore((state) => state.startAnimation);
  const setAudio = useStore((state) => state.setAudio);

  const handleClick = () => {
    startAnimation();
    const bgMusic = new Audio('/bgm/bgm.mp3');
    bgMusic.volume = 0.7;
    bgMusic.loop = true; 
    bgMusic.play().catch(error => {
      console.log("Audio play failed:", error);
    });
    
    setAudio(bgMusic);
    
    document.getElementById('enter-button').style.opacity = '0';
    setTimeout(() => {
      document.getElementById('enter-button').style.display = 'none';
    }, 200);
  };

  if (!isVisible) return null;

  return (
    <div 
      id="enter-button" 
      className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 animate-fade-in"
    >
      <button
        onClick={handleClick}
        className="group relative inline-flex h-14 items-center justify-center overflow-hidden rounded-lg border border-[#9181B7] bg-black bg-opacity-50 backdrop-blur-sm min-w-64 animate-bounce-slow"
      >
        {/* Default text state */}
        <div className="inline-flex h-14 translate-y-0 items-center justify-center px-8 text-[#9181B7] transition duration-500 group-hover:-translate-y-[150%]">
          <span className="text-lg font-medium tracking-wider whitespace-nowrap">ENTER $FITPIC</span>
        </div>
        
        {/* Hover state with animated background */}
        <div className="absolute inline-flex h-14 w-full translate-y-[100%] items-center justify-center text-white transition duration-500 group-hover:translate-y-0">
          <span className="absolute h-full w-full translate-y-full skew-y-12 scale-y-0 bg-[#9181B7] transition duration-500 group-hover:translate-y-0 group-hover:scale-150"></span>
          <span className="z-10 text-lg font-medium tracking-wider whitespace-nowrap text-black">ENTER $FITPIC</span>
        </div>
      </button>
    </div>
  );
}


function App() {
  const isPortrait = useOrientation();
  const [assetsLoaded, setAssetsLoaded] = useState(false);

  // Complete return function for App.jsx
  return (
    <div style={{ width: '100%', height: '100vh', position: 'fixed' }}>
      <div className="absolute w-full top-[10%] lg:top-[5%] text-center z-10 px-4">
        <div className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-wider leading-tight drop-shadow-lg">
           "DON'T WORRY ABOUT THE MONEY... JUST SEND A FIRE <span className="italic"> $FITPIC."</span>
          <br/><br/>
          <div className="flex items-center justify-center gap-2">
            NOW ON <img src={solanaLogo} alt="solana" className="ml-2 inline-block h-8 sm:h-9 md:h-10 lg:h-12 w-auto" />
          </div>
        </div>
      </div>

   
      <AssetPreloader onLoadComplete={() => setAssetsLoaded(true)}>
        <LoadingOverlay />
        <EnterButton isVisible={assetsLoaded} />

        <div className="fixed z-50 bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col gap-4 items-center">
          <div><SubmitFitPicButton /></div>
          <div><LookbookButton /></div>
          <div><XButton /></div>
          <div><DexscreenerButton /></div>
              {/* Credit text */}
       <div className="-mb-4 z-50 text-[#313337] text-lg">
        <span>design by <a href="https://x.com/_invntiv_" target="_blank" rel="noopener noreferrer" className="hover:text-white text-[#9181B7] transition-colors animate-pulse">invntiv</a></span>
       </div>

        </div>

        <LookbookModal />
                                                                                                                           
        <AudioControls />
        <Suspense fallback={null}>
          <Canvas
            camera={{ 
              position: [0, 0, isPortrait ? mobileSettings.cameraZ : desktopSettings.cameraZ], 
              fov: isPortrait ? mobileSettings.fov : desktopSettings.fov 
            }}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            dpr={[1, 2]}
            gl={{ 
              powerPreference: "high-performance",
              alpha: false,
              antialias: true,
              stencil: false,
              depth: true
            }}
          >
            <color attach="background" args={['#202020']} />
            <ambientLight intensity={0.1} />
            <pointLight position={[10, 10, 10]} intensity={10.0} color="#9370DB" />
            
            <Suspense fallback={null}>
              <EnvironmentLoader />
              <CameraControls mobileSettings={mobileSettings} desktopSettings={desktopSettings} />
              <EnvironmentRotation />
              <group scale={isPortrait ? mobileSettings.scale : desktopSettings.scale}>
                <HomepageLogo />
              </group>
            </Suspense>
          </Canvas>
        </Suspense>
      </AssetPreloader>
    </div>
  );
}

export default App;